import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Status, Typography } from '@kc-console/common-ui';

import type { EventStatusType, RelatedServiceItem } from '@/pages/events/model/types';

import { getEventStatusVariant } from '@/pages/events/model/getEventStatusVariant';

interface RelatedServicesSectionContentProps {
  services: RelatedServiceItem[];
  eventStatus?: EventStatusType;
}

interface NormalizedService {
  name: string;
  status?: EventStatusType;
}

/**
 * relatedServices 원소를 `{ name, status }` 형태로 정규화한다.
 * @description
 * - API가 문자열 배열 또는 객체 배열(`serviceName`/`name`/`title`)을 모두 내려줄 수 있어 방어적으로 처리한다.
 */
const normalizeService = (service: RelatedServiceItem): NormalizedService => {
  if (typeof service === 'string') {
    return { name: service };
  }

  return {
    name: service.serviceName ?? service.name ?? service.title ?? '',
    status: service.serviceStatus,
  };
};

/**
 * 이벤트 상세 - 대상 서비스 섹션.
 * @description
 * - 관련 서비스 목록을 4열 카드 그리드로 노출하고, 각 카드에 상태 아이콘을 함께 표시한다.
 * - 서비스 원소에 자체 `status`가 있으면 그 값으로 아이콘을 매핑하고, 없으면 이벤트 전체 상태를 사용한다.
 * - 서비스가 없으면 빈 상태 메시지를 보여준다.
 * @example
 * <RelatedServicesSectionContent services={['VPC']} eventStatus="OPENED" />
 */
const RelatedServicesSectionContent = ({
  eventStatus,
  services,
}: RelatedServicesSectionContentProps) => {
  if (services.length === 0) {
    return (
      <EmptyRoot>
        <Typography variant="body/normal/md" color="text/weak/default">
          대상 서비스가 없습니다.
        </Typography>
      </EmptyRoot>
    );
  }

  const fallbackVariant = eventStatus ? getEventStatusVariant(eventStatus) : 'active';

  return (
    <Grid>
      {services.map((service, index) => {
        const { name, status } = normalizeService(service);
        const variant = status ? getEventStatusVariant(status) : fallbackVariant;
        return (
          <Card key={`${name}-${index}`}>
            <Status status={variant} hideLabel />
            <ServiceName variant="label/semibold/lg">{name || '-'}</ServiceName>
          </Card>
        );
      })}
    </Grid>
  );
};

export default RelatedServicesSectionContent;

const Grid = styled('ul')`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Card = styled('li')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 56px;
    padding: 16px 20px;
    background-color: ${theme.color['surface/default']};
    border-radius: 2px;
    box-shadow: ${theme.elevation.level_1};
  `,
);

const ServiceName = styled(Typography)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyRoot = styled('div')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background-color: ${theme.color['surface/neutral/weaker/default']};
    border-radius: 2px;
  `,
);
